import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ClockCounterClockwise, MagnifyingGlass, Trash, PencilSimple, Signature,
  ArrowClockwise, Eye, Funnel, FileXls, FilePdf,
} from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * `/admin/audit-history` — historial global de capturas eliminadas / editadas
 * críticamente. Solo Admin/Superadmin (protegido en el backend con
 * `security.audit` y en frontend con adminOnly).
 *
 * Consume `records_audit` vía:
 *  - GET /api/audit/modules         → conteos por módulo (para filtros)
 *  - GET /api/audit/records         → feed general filtrable
 */

// Etiquetas legibles por módulo — cubre TODOS los módulos que registran audit.
const MODULE_LABEL = {
  hr_attendance: "Asistencia",
  hr_employees: "Colaboradores",
  hr_crews: "Cuadrillas",
  hr_custom_kpis: "KPIs personalizados",
  hse_daily_reports: "Reportes diarios SGSSO",
  hse_hazards: "Peligros (catálogo)",
  hse_iper: "IPER-C",
  hse_bowtie: "Bow-Tie",
  hse_jsa: "JSA/AST",
  hse_permits: "Permisos de trabajo",
  hse_observations: "Observaciones BBS",
  hse_toolbox: "Charlas Toolbox",
  hse_investigations: "Investigaciones",
  hse_actions: "Acciones correctivas",
  hse_medical_tests: "Pruebas médicas",
  bitacora: "Bitácora",
  bitacora_photo: "Fotos de bitácora",
  epp_deliveries: "Entregas de EPP",
  projects: "Proyectos",
  tasks: "Tareas (Gantt)",
  costs: "Costos de proyecto",
  operations_entries: "Reportes de operación",
  operations_budgets: "Budgets operativos",
  operations_front_plans: "Planes por obra",
  inventory_items: "Items de inventario",
  inventory_movements: "Movimientos (Kardex)",
  finance_daily_entries: "Producción diaria (Finance)",
  finance_client_estimates: "Estimaciones cliente",
  finance_ledger: "Libro mayor",
  purchase_orders: "Órdenes de compra",
  maint_equipment: "Flota (equipos)",
  maint_components: "Componentes",
  maint_kits: "Kits de refaccionamiento",
  maint_orders: "OTs de mantenimiento",
  maint_failures: "Fallas de mantenimiento",
  maint_events: "Eventos de mantenimiento",
  maint_equipment_costs: "Costos de equipo",
  maint_findings: "Hallazgos de inspección",
  maint_inspections: "Inspecciones",
  camp_rooms: "Habitaciones",
  camp_assignments: "Asignaciones de campamento",
  camp_laundry: "Lavandería",
};

const ACTION_LABEL = {
  delete: { label: "Eliminado", tone: "bg-red-100 text-red-700 border-red-300", icon: Trash },
  archive: { label: "Archivado", tone: "bg-amber-100 text-amber-700 border-amber-300", icon: Trash },
  cancel: { label: "Cancelado", tone: "bg-orange-100 text-orange-700 border-orange-300", icon: Trash },
  edit_critical: { label: "Edición crítica", tone: "bg-blue-100 text-blue-700 border-blue-300", icon: PencilSimple },
  unsign: { label: "Firma anulada", tone: "bg-purple-100 text-purple-700 border-purple-300", icon: Signature },
  sign: { label: "Firmado", tone: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: Signature },
  approve: { label: "Aprobado", tone: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: Signature },
  reject: { label: "Rechazado", tone: "bg-red-100 text-red-700 border-red-300", icon: Trash },
  restore: { label: "Restaurado", tone: "bg-teal-100 text-teal-700 border-teal-300", icon: ArrowClockwise },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-PE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
}

function ModuleLabel({ code }) {
  return <span>{MODULE_LABEL[code] || code}</span>;
}

function ActionBadge({ action }) {
  const meta = ACTION_LABEL[action] || { label: action, tone: "bg-slate-100 text-slate-700 border-slate-300", icon: PencilSimple };
  const Ico = meta.icon;
  return (
    <Badge className={`gap-1 ${meta.tone}`}>
      <Ico size={11} weight="bold" /> {meta.label}
    </Badge>
  );
}

export default function AuditHistory() {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterModule, setFilterModule] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [filterActor, setFilterActor] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      q.set("limit", "500");
      if (filterModule !== "all") q.set("module", filterModule);
      if (filterAction !== "all") q.set("action", filterAction);
      if (filterActor.trim()) q.set("actor_email", filterActor.trim());
      if (filterFrom) q.set("date_from", filterFrom);
      if (filterTo) q.set("date_to", filterTo);
      const [rs, ms] = await Promise.all([
        api.get(`/audit/records?${q.toString()}`),
        api.get(`/audit/modules`),
      ]);
      setRows(rs.data || []);
      setModules(ms.data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterModule, filterAction, filterFrom, filterTo]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase().trim();
    return rows.filter((r) => (
      (r.actor_name || "").toLowerCase().includes(q)
      || (r.actor_email || "").toLowerCase().includes(q)
      || (r.record_id || "").toLowerCase().includes(q)
      || (r.reason || "").toLowerCase().includes(q)
      || (MODULE_LABEL[r.module] || r.module || "").toLowerCase().includes(q)
    ));
  }, [rows, search]);

  const totalsByAction = useMemo(() => {
    const acc = {};
    for (const r of rows) acc[r.action] = (acc[r.action] || 0) + 1;
    return acc;
  }, [rows]);

  const buildFilterQuery = () => {
    const q = new URLSearchParams();
    if (filterModule !== "all") q.set("module", filterModule);
    if (filterAction !== "all") q.set("action", filterAction);
    if (filterActor.trim()) q.set("actor_email", filterActor.trim());
    if (filterFrom) q.set("date_from", filterFrom);
    if (filterTo) q.set("date_to", filterTo);
    return q;
  };

  const exportXlsx = async () => {
    const q = buildFilterQuery();
    try {
      const res = await api.get(`/audit/records/export.xlsx?${q.toString()}`, { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      await downloadFile(res.data, `historial-capturas-${stamp}.xlsx`);
      toast.success("XLSX descargado — comparte con la auditora minera");
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportPdf = async () => {
    const q = buildFilterQuery();
    try {
      const res = await api.get(`/audit/records/export.pdf?${q.toString()}`, { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      await downloadFile(res.data, `historial-capturas-${stamp}.pdf`);
      toast.success("PDF descargado");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="audit-history-page">
      <header className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight text-slate-900 flex items-center gap-2">
            <ClockCounterClockwise size={28} weight="fill" className="text-slate-800" />
            Historial de Capturas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Registro append-only de todas las eliminaciones, ediciones críticas y firmas anuladas
            a lo largo de la plataforma. Conservado indefinidamente para auditoría minera.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportXlsx}
                  data-testid="audit-export-xlsx-btn" className="gap-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50">
            <FileXls size={15} weight="bold" /> XLSX
          </Button>
          <Button variant="outline" onClick={exportPdf}
                  data-testid="audit-export-pdf-btn" className="gap-2 border-red-300 text-red-800 hover:bg-red-50">
            <FilePdf size={15} weight="bold" /> PDF
          </Button>
          <Button variant="outline" onClick={load} data-testid="audit-refresh-btn" className="gap-2">
            <ArrowClockwise size={15} /> Refrescar
          </Button>
        </div>
      </header>

      {/* KPIs por acción */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="audit-kpis">
        {Object.entries(ACTION_LABEL).slice(0, 5).map(([code, meta]) => (
          <Card key={code} className="p-3">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">{meta.label}</p>
            <p className="font-heading font-black text-2xl tabular-nums text-slate-900 mt-1">
              {totalsByAction[code] || 0}
            </p>
          </Card>
        ))}
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Funnel size={16} weight="fill" className="text-slate-600" />
          <h2 className="font-heading font-black text-sm uppercase tracking-widest text-slate-700">Filtros</h2>
        </div>
        <div className="grid md:grid-cols-6 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Módulo</label>
            <Select value={filterModule} onValueChange={setFilterModule}>
              <SelectTrigger data-testid="filter-module" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos ({rows.length})</SelectItem>
                {modules.map((m) => (
                  <SelectItem key={m.module} value={m.module}>
                    {MODULE_LABEL[m.module] || m.module} ({m.total})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Acción</label>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger data-testid="filter-action" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(ACTION_LABEL).map(([code, meta]) => (
                  <SelectItem key={code} value={code}>{meta.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Actor (email)</label>
            <Input value={filterActor} onChange={(e) => setFilterActor(e.target.value)}
                    onBlur={load}
                    placeholder="usuario@empresa.com"
                    className="mt-1" data-testid="filter-actor" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Desde</label>
            <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
                    className="mt-1" data-testid="filter-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Hasta</label>
            <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
                    className="mt-1" data-testid="filter-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Buscar</label>
            <div className="relative mt-1">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="Cualquier campo…" className="pl-7" data-testid="filter-search" />
            </div>
          </div>
        </div>
      </Card>

      {/* Tabla */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-black text-sm uppercase tracking-widest text-slate-700">
            {filtered.length} registro{filtered.length === 1 ? "" : "s"}
          </h2>
          {loading && <span className="text-xs text-slate-400">Cargando…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full data-table text-sm" data-testid="audit-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Módulo</th>
                <th>Acción</th>
                <th>ID del registro</th>
                <th>Actor</th>
                <th>Motivo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8">
                  {loading ? "Cargando…" : "Sin registros con esos filtros."}
                </td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                  <td className="text-xs font-mono whitespace-nowrap">{fmtDate(r.at)}</td>
                  <td className="text-xs"><ModuleLabel code={r.module} /></td>
                  <td><ActionBadge action={r.action} /></td>
                  <td className="text-[11px] font-mono text-slate-500 max-w-[180px] truncate" title={r.record_id}>{r.record_id}</td>
                  <td className="text-xs">
                    <div className="font-medium text-slate-800">{r.actor_name || "—"}</div>
                    <div className="text-[10px] text-slate-500">{r.actor_email || ""}</div>
                  </td>
                  <td className="text-xs text-slate-600 max-w-md truncate" title={r.reason}>
                    {r.reason || <span className="text-slate-400">—</span>}
                  </td>
                  <td>
                    <Button variant="outline" size="sm" className="gap-1"
                            onClick={() => setDetail(r)}
                            data-testid={`view-audit-${r.id}`}>
                      <Eye size={12} /> Ver
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detalle */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" aria-describedby="audit-detail-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise size={20} weight="fill" />
              Detalle · <ModuleLabel code={detail?.module} />
            </DialogTitle>
            <DialogDescription id="audit-detail-desc">
              Snapshot inmutable del registro al momento de la acción, con el actor
              y la fecha para auditoría minera.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm" data-testid="audit-detail">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">Fecha</p>
                  <p className="font-mono">{fmtDate(detail.at)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">Acción</p>
                  <p><ActionBadge action={detail.action} /></p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">Actor</p>
                  <p className="font-medium">{detail.actor_name || "—"}</p>
                  <p className="text-xs text-slate-500">{detail.actor_email || ""}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">ID de registro</p>
                  <p className="font-mono text-xs break-all">{detail.record_id}</p>
                </div>
                {detail.reason && (
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">Motivo</p>
                    <p>{detail.reason}</p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Snapshot del registro</p>
                <pre className="text-[11px] bg-slate-100 border border-slate-200 rounded p-3 max-h-96 overflow-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(detail.snapshot || {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
